import { PageAccess, permissionMap } from "@login";
import { Grupo, Filial } from "@apiUser";

// --- Funções de grupos/filiais/empresas ---

export function explodeGroups(grupos: Grupo[] | undefined) {
  const filialMap = new Map<string, Filial>();
  const empresaSet = new Set<string>();
  const gruposApi: Array<{ codigo: string; descricao: string }> = [];

  if (!grupos?.length) {
    return { filiais: [], empresas: [], gruposApi: [] };
  }

  grupos.forEach((grupo) => {
    gruposApi.push({
      codigo: grupo.codigo,
      descricao: grupo.descricao,
    });

    grupo.empresas.forEach((empresa) => {
      if (empresa.codigo && empresa.codigo !== "todas") {
        empresaSet.add(empresa.codigo);
      }
      empresa.filiais.forEach((filial) => {
        filialMap.set(filial.codigo, filial);
      });
    });
  });

  return {
    filiais: Array.from(filialMap.values()).sort((a, b) => a.codigo.localeCompare(b.codigo)),
    empresas: Array.from(empresaSet),
    gruposApi,
  };
}

export function extractTenantId(grupos: Grupo[]): string {
  if (!grupos?.length) return "01";
  for (const grupo of grupos) {
    for (const empresa of grupo.empresas) {
      if (
        empresa.codigo &&
        empresa.codigo !== "todas" &&
        empresa.filiais.length > 0
      ) {
        // Usa a segunda filial se disponível, senão usa a primeira
        const filialIndex = empresa.filiais.length > 1 ? 1 : 0;
        const filial = empresa.filiais[filialIndex];
        return `${empresa.codigo},${filial.codigo}`;
      }
    }
  }
  return "01";
}

export function extractUniqueFiliais(grupos: Grupo[]): Filial[] {
  const filialMap = new Map<string, Filial>();
  grupos.forEach((grupo) => {
    grupo.empresas.forEach((empresa) => {
      empresa.filiais.forEach((filial) => {
        filialMap.set(filial.codigo, filial);
      });
    });
  });
  return Array.from(filialMap.values());
}

export function extractUniqueEmpresas(grupos: Grupo[]): string[] {
  const empresaSet = new Set<string>();
  grupos.forEach((grupo) => {
    grupo.empresas.forEach((empresa) => {
      if (empresa.codigo && empresa.codigo !== "todas") {
        empresaSet.add(empresa.codigo);
      }
    });
  });
  return Array.from(empresaSet);
}

export function findFilialByCode(
  grupos: Grupo[],
  filialCodigo: string
): Filial | undefined {
  for (const grupo of grupos) {
    for (const empresa of grupo.empresas) {
      const filial = empresa.filiais.find((f) => f.codigo === filialCodigo);
      if (filial) return filial;
    }
  }
  return undefined;
}

export function findFiliaisByEmpresa(
  grupos: Grupo[],
  empresaCodigo: string
): Filial[] {
  const filiais: Filial[] = [];
  grupos.forEach((grupo) => {
    const empresa = grupo.empresas.find((e) => e.codigo === empresaCodigo);
    if (empresa) {
      filiais.push(...empresa.filiais);
    }
  });
  return filiais;
}

// --- Funções de permissões ---

export function calculatePageAccess(groupIds: string[]): PageAccess {
  const pageAccess: PageAccess = {
    isAdmin: false,
    isContab: false,
    isCompras: false,
    isBorracharia: false,
    isPortaria: false,
  };
  for (const key in permissionMap) {
    const permissionKey = key as keyof PageAccess;
    const requiredCodes = permissionMap[permissionKey];
    if (requiredCodes.some((code) => groupIds.includes(code))) {
      pageAccess[permissionKey] = true;
    }
  }
  return pageAccess;
}

export function hasPermission(
  groupIds: string[],
  permission: keyof PageAccess
): boolean {
  const requiredCodes = permissionMap[permission];
  return requiredCodes.some((code) => groupIds.includes(code));
}

export function getActivePermissions(groupIds: string[]): (keyof PageAccess)[] {
  const permissions: (keyof PageAccess)[] = [];
  for (const key in permissionMap) {
    const permissionKey = key as keyof PageAccess;
    if (hasPermission(groupIds, permissionKey)) {
      permissions.push(permissionKey);
    }
  }
  return permissions;
}

/**
 * Verifica o status da senha com base na data da última alteração.
 * @param lastChange - Data no formato YYYYMMDD.
 * @param validityDays - Quantidade de dias de validade da senha (default: 90).
 * @returns Número de dias restantes até expirar (pode ser negativo se já expirou).
 * @example
 * const remaining = checkPasswordExpiration("20250804");
 * if (remaining <= 0) toast.error("Senha expirada");
 */
export function checkPasswordExpiration(
  lastChange: string | undefined,
  validityDays = 90
): number {
  if (!lastChange) return 0;

  const year = Number(lastChange.slice(0, 4));
  const month = Number(lastChange.slice(4, 6)) - 1;
  const day = Number(lastChange.slice(6, 8));

  const lastDate = new Date(year, month, day);
  const today = new Date();
  const daysGone = Math.floor(
    (today.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24)
  );

  return validityDays - daysGone;
}
